import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { useCompleteOnboarding } from '@/api/hooks/useOnboarding';
import type { LedgerType, OnboardingCollectionInput } from '@/api/types';

interface CollectionOption {
    label: string;
    description: string;
}

const ASSET_COLLECTIONS: CollectionOption[] = [
    { label: 'Investment', description: 'Stocks, bonds, ETFs' },
    { label: 'Real Estate', description: 'Properties and REITs' },
    { label: 'Fixed', description: 'Savings, CDs' },
    { label: 'Depreciable', description: 'Vehicles, equipment' },
];

const DEBT_COLLECTIONS: CollectionOption[] = [
    { label: 'Mortgage', description: 'Home loans' },
    { label: 'Student Loan', description: 'Education debt' },
    { label: 'Credit Card', description: 'Credit card balances' },
    { label: 'Auto', description: 'Vehicle loans' },
];

interface TypeSetupPromptProps {
    type: LedgerType;
    onComplete: () => void;
}

export function TypeSetupPrompt({ type, onComplete }: TypeSetupPromptProps) {
    const { refreshProfile } = useAuth();
    const completeOnboarding = useCompleteOnboarding();
    const [selected, setSelected] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    const collections = type === 'asset' ? ASSET_COLLECTIONS : DEBT_COLLECTIONS;
    const typeLabel = type === 'asset' ? 'Asset' : 'Debt';
    const typeLabelPlural = type === 'asset' ? 'Assets' : 'Debts';

    const toggle = (label: string) => {
        setSelected((prev) =>
            prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
        );
    };

    const handleSubmit = async () => {
        if (selected.length === 0) {
            setError('Please select at least one collection.');
            return;
        }

        setError(null);

        const collectionsToCreate: OnboardingCollectionInput[] = selected.map((label) => {
            return { label, type };
        });

        try {
            await completeOnboarding.mutateAsync({ collections: collectionsToCreate });
            await refreshProfile();
            onComplete();
        } catch {
            setError('Failed to create collections. Please try again.');
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary p-8">
            <div className="max-w-xl mx-auto text-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Set Up Your {typeLabelPlural}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    You don't have any {typeLabel.toLowerCase()} collections yet.
                    <br />
                    Select the types of {typeLabelPlural.toLowerCase()} you want to track.
                </p>

                <div className="grid gap-2 text-left mb-6">
                    {collections.map((collection) => (
                        <label
                            key={collection.label}
                            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                        >
                            <Checkbox
                                checked={selected.includes(collection.label)}
                                onChange={() => toggle(collection.label)}
                                className="shrink-0"
                            />
                            <span className="text-gray-900 dark:text-white">
                                <span className="font-medium">{collection.label}</span>
                                <span className="text-sm text-gray-500 dark:text-gray-400"> — {collection.description}</span>
                            </span>
                        </label>
                    ))}
                </div>

                {error && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {error}
                    </div>
                )}

                <Button
                    onClick={handleSubmit}
                    isLoading={completeOnboarding.isPending}
                    disabled={selected.length === 0}
                    className="w-full"
                    size="lg"
                >
                    {selected.length > 0
                        ? `Create ${selected.length} Collection${selected.length > 1 ? 's' : ''}`
                        : 'Select Collections to Continue'}
                </Button>

                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    You can customize and add more collections later in{' '}
                    <Link to="/settings/attribution" className="text-accent hover:underline">
                        Settings
                    </Link>
                    .
                </p>
            </div>
        </div>
    );
}
